import React, { useState, useEffect, Fragment } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  CustomInput,
  InputGroup,
  InputGroupText,
  InputGroupAddon,
} from "reactstrap";
import { AxiosAPI } from "../../../../../../helpers/AxiosAPI";
import { gAPI_Twilio_Templates } from "../../../../../../constants/APIs";

const CHANNEL_OPTIONS = ["WhatsApp", "SMS"];

const FIELDS = [
  { key: "guest_first_name", label: "First Name" },
  { key: "guest_last_name", label: "Last Name" },
  { key: "check_in_date", label: "Check-in Date" },
  { key: "check_out_date", label: "Check-out Date" },
  { key: "guest_email", label: "Email Address" },
  { key: "guest_phone", label: "Phone Number" },
  { key: "room_number", label: "Room Number" },
  { key: "room_type", label: "Room Type Name" },
];

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ar", label: "Arabic" },
  { code: "fr", label: "French" },
  { code: "es", label: "Spanish" },
  { code: "pt_BR", label: "Portuguese (Brazil)" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "id", label: "Indonesian" },
  { code: "vi", label: "Vietnamese" },
];

const TEMPLATE_TYPES = [
  { value: "text", label: "Text" },
  { value: "media", label: "Media (Coming Soon)" },
  { value: "cta", label: "Call to Action (Coming Soon)" },
  { value: "card", label: "Card (Coming Soon)" },
  { value: "quick_reply", label: "Quick Reply (Coming Soon)" },
];



const AddNewTemplate = ({
  propertyId,
  groupId,
  userName,
  visible,
  toggleModal,
  formLabel,
  templateData,
  refreshTemplates,
}) => {
  const [name, setName] = useState("");
  const [channels, setChannels] = useState([]);
  const [message, setMessage] = useState("");
  const [placeholderMap, setPlaceholderMap] = useState({});
  const [placeholderErrors, setPlaceholderErrors] = useState({});
  const [templateId, setTemplateId] = useState(null);
  const [templateType, setTemplateType] = useState(null);
  const [language, setLanguage] = useState(null);
  /* ================= PREFILL ON EDIT ================= */
  useEffect(() => {
    if (templateData) {
      setName(templateData.name || "");
      setChannels(templateData.channel || []);
      setMessage(templateData.templateBody || "");
      setTemplateId(templateData.templateId || "");
      setTemplateType(templateData.templateType || "");
      setLanguage((templateData.language || "").toLowerCase());

      const map = {};
      (templateData.placeholders || []).forEach(p => {
        map[p.name] = p.value;
      });
      setPlaceholderMap(map);
      setPlaceholderErrors({});
    } else {
      setName("");
      setChannels([]);
      setMessage("");
      setTemplateId("");
      setTemplateType("");
      setLanguage("");
      setPlaceholderMap({});
      setPlaceholderErrors({});
    }
  }, [templateData, visible]);

  const handleChannelChange = (channel) => {
    setChannels(prev =>
      prev.includes(channel)
        ? prev.filter(c => c !== channel)
        : [...prev, channel]
    );
  };
  const validatePlaceholders = () => {
    const placeholdersInMessage = extractPlaceholders(message);
    const errors = {};

    placeholdersInMessage.forEach(ph => {
      if (!placeholderMap[ph]) {
        errors[ph] = "This field is required";
      }
    });

    setPlaceholderErrors(errors);

    return Object.keys(errors).length === 0;
  };
  const extractPlaceholders = (text) =>
    [...new Set(
      (text.match(/{{\s*([\w-]+)\s*}}/g) || [])
        .map(p => p.replace(/[{}]/g, ""))
    )];


  /* ================= CREATE ================= */
  const handleCreateTemplate = async () => {
    if (!validatePlaceholders()) return;
    const placeholders = Object.entries(placeholderMap).map(
      ([name, value]) => ({ name, value })
    );

    const body = {
      groupId,
      propertyId,
      name,
      templateId: templateId,
      templateBody: message,
      channel: channels,
      templateType: templateType,
      language: (language || "").toLowerCase(),
      createdBy: userName,
      placeholders,
    };

    try {
      await AxiosAPI({
        method: "post",
        url: gAPI_Twilio_Templates,
        data: body,
      });

      toggleModal();
      refreshTemplates?.();
    } catch (err) {
      console.error("Failed to create template", err);
    }
  };

  /* ================= UPDATE ================= */
  const handleUpdateTemplate = async () => {
    if (!validatePlaceholders()) return;
    const placeholders = Object.entries(placeholderMap).map(
      ([name, value]) => ({ name, value })
    );

    const body = {
      name,
      templateBody: message,
      templateType: templateType,
      templateId: templateId,
      language: (language || "").toLowerCase(),
      channel: channels,
      updatedBy: userName,
      placeholders,
    };

    try {
      await AxiosAPI({
        method: "put",
        url: `${gAPI_Twilio_Templates}/${templateData.id}`,
        params: {
          group_id: groupId,
        },
        data: body,
      });

      toggleModal();
      refreshTemplates?.();
    } catch (error) {
      console.error(
        "Failed to update template",
        error?.response?.data || error
      );
    }
  };

  const handleSave = () => {
    templateData ? handleUpdateTemplate() : handleCreateTemplate();
  };
  useEffect(() => {
    if (templateData) {
      // UPDATE
      setName(templateData.name || "");
      setChannels(templateData.channel || []);
      setMessage(templateData.templateBody || "");
    } else {
      // CREATE
      setName("");
      setChannels([]);
      setMessage("");
      setPlaceholderMap({});
      setPlaceholderErrors({});
    }
  }, [templateData, visible]);

  const isWhatsApp = channels.includes("WhatsApp");
  const isSMSOnly = channels.includes("SMS") && !isWhatsApp;
  const noChannelSelected = channels.length === 0;

  return (
    <Fragment>
      <style>
        {`
      select option:disabled {
        background-color: #d0d0d0;
        color: #555;
        cursor: not-allowed;
      }
    `}
      </style>
      <Modal isOpen={visible} toggle={toggleModal} wrapClassName="modal-right">
        <ModalHeader toggle={toggleModal}>{formLabel}</ModalHeader>

        <ModalBody>
          <Label>Name</Label>
          <Input className='input_sty' value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter Twilio Template Name" />

          <Label className="mt-3">Template Id</Label>
          <Input className='input_sty'
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            placeholder="Enter Twilio Template ID"
          />
          <Label className="mt-3">Template Type</Label>
          <Input className='input_sty'
            type="select"
            value={templateType || ""}
            onChange={(e) => setTemplateType(e.target.value)}
          >
            <option value="">Select Template Type</option>

            {TEMPLATE_TYPES
              .filter(t =>
                (noChannelSelected || isSMSOnly)
                  ? t.value === "text"
                  : true
              )
              .map(t => {
                const disabled = isWhatsApp && t.value !== "text";

                return (
                  <option
                    key={t.value}
                    value={t.value}
                    disabled={disabled}
                  >
                    {t.label}
                  </option>
                );
              })}
          </Input>
          <Label className="mt-3">Language</Label>
          <Input className='input_sty'
            type="select"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option value="">Select Language</option>
            {LANGUAGES.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </Input>

          <Label className="mt-3">Channels</Label>
          {CHANNEL_OPTIONS.map((ch) => (
            <CustomInput
              key={ch}
              type="checkbox"
              label={ch}
              checked={channels.includes(ch)}
              onChange={() => handleChannelChange(ch)}
            />
          ))}

          <Label className="mt-3">Message</Label>
          <Input className='input_sty mb-2'
            type="textarea"
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter Twilio Template Message"
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
            }}
          >
            {extractPlaceholders(message).map((ph) => {
              const hasError = placeholderErrors[ph];

              return (
                <div key={ph} style={{ marginBottom: 5 }}>
                  <InputGroup
                    style={{
                      border: hasError ? "1px solid red" : "1px solid #ddd",
                      borderRadius: 6,
                    }}
                  >
                    {/* LEFT: Placeholder */}
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText
                        style={{
                          background: "#f8f9fa",
                          fontWeight: 600,
                          minWidth: 120,
                          justifyContent: "center",
                        }}
                      >
                        {`{{${ph}}}`}
                      </InputGroupText>
                    </InputGroupAddon>

                    {/* RIGHT: Dropdown */}
                    <Input className='input_sty'
                      type="select"
                      value={placeholderMap[ph] || ""}
                      onChange={(e) => {
                        setPlaceholderMap({
                          ...placeholderMap,
                          [ph]: e.target.value,
                        });

                        setPlaceholderErrors((prev) => {
                          const copy = { ...prev };
                          delete copy[ph];
                          return copy;
                        });
                      }}
                    >
                      <option value="">Select Field</option>
                      {FIELDS.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}
                        </option>
                      ))}
                    </Input>
                  </InputGroup>

                  {/* ERROR MESSAGE */}
                  {hasError && (
                    <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                      Selection required
                    </div>
                  )}
                </div>
              );
            })}

          </div>


        </ModalBody>

        <ModalFooter>
          <Button color="primary" onClick={handleSave}>
            {templateData ? "Update" : "Save"}
          </Button>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default AddNewTemplate;